import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Share2, Copy, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ShippingCalculator from "@/components/ShippingCalculator";
import { useLanguage } from "@/context/LanguageContext";
import { CANONICAL_ORIGIN } from "@/data/translations";
import { COUNTRIES } from "@/data/stores";
import { trackEvent } from "@/lib/analytics";

const EMBED_ROUTE_CONFIG = {
  "shipping-rates": {
    srcPath: "/embeds/shipping-rates.html",
    titleKey: "shippingRates",
    testIdBase: "shipping-rates",
  },
  "shipping-only": {
    srcPath: "/embeds/shipping-only.html",
    titleKey: "shippingOnly",
    testIdBase: "shipping-only",
  },
  "china-suppliers": {
    srcPath: "/embeds/china-suppliers.html",
    titleKey: "chinaSuppliers",
    testIdBase: "china-suppliers",
    backHref: "/country/cn",
  },
};

const EMBED_CALCULATOR_COUNTRY = COUNTRIES.CN;

/**
 * Generic wrapper for a standalone BOB-branded HTML file.
 * - Shows full BOB Navbar + Footer + FloatingWhatsApp
 * - Embeds the HTML file in an iframe that grows to content height
 * - Provides shareable URL + copy-to-clipboard + back link
 */
export default function EmbedPage({
  srcPath,
  titleKey,
  testIdBase,
  backHref = "/",
}) {
  const { t, lang, isRTL } = useLanguage();
  const location = useLocation();
  const routeMatch = location.pathname.match(/^\/embed\/([^/?#]+)/);
  const embedKey = routeMatch?.[1] || "";
  const routeConfig = !srcPath ? EMBED_ROUTE_CONFIG[embedKey] : null;
  const isInlineCalculator = !srcPath && embedKey === "shipping-calculator";
  const resolvedSrcPath = srcPath || routeConfig?.srcPath || "";
  const resolvedTitleKey = titleKey || routeConfig?.titleKey || "";
  const resolvedBackHref = routeConfig?.backHref || backHref;
  const key = testIdBase || routeConfig?.testIdBase || embedKey || "embed";
  const label = isInlineCalculator
    ? (t.calculator && t.calculator.title) || "Shipping Calculator"
    : (t.embeds && t.embeds[resolvedTitleKey]) || resolvedTitleKey || "Embed";
  const common = t.embeds || {};

  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(600);
  const iframeRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setShareUrl(`${CANONICAL_ORIGIN}${location.pathname}`);
    if (typeof document !== "undefined") {
      document.title = `${label} — BOB Services LB`;
    }
  }, [label, location.pathname]);

  useEffect(() => {
    if (isInlineCalculator) return;
    const routeName = embedKey ? `embed_${embedKey}` : key;
    if (resolvedTitleKey === "shippingRates") {
      trackEvent("shipping_rates_view", {
        routeName,
        metadata: { source: embedKey ? "embed" : "page" },
      });
    }
    if (resolvedTitleKey === "shippingOnly") {
      trackEvent("shipping_only_view", {
        routeName,
        metadata: { source: embedKey ? "embed" : "page" },
      });
      trackEvent("shipping_only_open", {
        routeName,
        metadata: { source: embedKey ? "embed" : "page" },
      });
    }
    if (resolvedTitleKey === "chinaSuppliers") {
      trackEvent("china_suppliers_section_view", {
        routeName,
        metadata: { source: embedKey ? "embed" : "page" },
      });
    }
  }, [embedKey, isInlineCalculator, key, resolvedTitleKey]);

  // Auto-resize the iframe to its content height so the page scrolls
  // naturally instead of trapping content inside an internal scrollbar.
  // The embedded HTML is served from the SAME origin → we can safely read
  // `contentDocument` and observe its body. To avoid ResizeObserver loop
  // warnings, we deliberately use staged setTimeout measurements (catches
  // image/font loads) + a throttled MutationObserver for tab switches.
  useEffect(() => {
    if (!resolvedSrcPath) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let mutationObserver;
    let throttleId = 0;
    let stagedTimers = [];
    let lastReported = 0;
    let detachEmbedListeners;

    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement.scrollHeight || 0,
          doc.body?.scrollHeight || 0
        );
        // Threshold breaks any potential observer→state→observer cascade
        if (h && Math.abs(h - lastReported) > 16) {
          lastReported = h;
          setIframeHeight(h);
        }
      } catch (err) {
        // cross-origin or doc not ready — graceful fallback
      }
    };

    const throttledMeasure = () => {
      if (throttleId) return;
      throttleId = window.setTimeout(() => {
        throttleId = 0;
        measure();
      }, 180);
    };

    const onLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      try {
        doc.documentElement.style.overflow = "visible";
        doc.body.style.overflow = "visible";
      } catch {
        // ignore
      }
      // Staged measurements catch images / fonts / late layout shifts
      [0, 200, 500, 1000, 2000, 4000].forEach((delay) => {
        stagedTimers.push(window.setTimeout(measure, delay));
      });
      // Throttled MutationObserver catches tab switches & collapsibles
      mutationObserver = new MutationObserver(throttledMeasure);
      mutationObserver.observe(doc.body, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: false,
      });
      // Re-measure when images finish loading
      doc.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", throttledMeasure, { once: true });
      });
      if (resolvedTitleKey === "shippingOnly") {
        const addListener = (selector, eventType, metadata) => {
          const node = doc.querySelector(selector);
          if (!node) return null;
          const handler = () => {
            trackEvent(eventType, {
              routeName: embedKey ? `embed_${embedKey}` : "shipping_only",
              metadata,
            });
          };
          node.addEventListener("click", handler);
          return () => node.removeEventListener("click", handler);
        };
        const listeners = [
          addListener("[data-testid='shein-copy-address']", "warehouse_copy_address", {
            source: "shipping_only_embed",
            target: "shein_address",
          }),
          addListener("[data-testid='shein-copy-mark']", "shipping_mark_copy", {
            source: "shipping_only_embed",
            target: "shein_mark",
          }),
          addListener("[data-testid='shein-wa-help']", "whatsapp_click", {
            source: "shipping_only_embed",
            target: "shein_help",
          }),
          addListener("[data-testid='shein-wa-help']", "contact_click", {
            source: "shipping_only_embed",
            target: "shein_help",
            channel: "whatsapp",
          }),
        ].filter(Boolean);
        detachEmbedListeners = () => listeners.forEach((dispose) => dispose());
      }
    };

    iframe.addEventListener("load", onLoad);
    window.addEventListener("resize", throttledMeasure);

    if (iframe.contentDocument && iframe.contentDocument.readyState === "complete") {
      onLoad();
    }

    return () => {
      iframe.removeEventListener("load", onLoad);
      window.removeEventListener("resize", throttledMeasure);
      stagedTimers.forEach((id) => window.clearTimeout(id));
      if (throttleId) window.clearTimeout(throttleId);
      if (mutationObserver) mutationObserver.disconnect();
      if (detachEmbedListeners) detachEmbedListeners();
    };
  }, [embedKey, lang, resolvedSrcPath, resolvedTitleKey]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("[Embed] clipboard write failed:", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: label, url: shareUrl });
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.warn("[Embed] navigator.share failed:", err);
        }
      }
    } else {
      handleCopy();
    }
  };

  // Pass current language to the embedded HTML so any new bilingual content
  // (e.g. the SHEIN destination card) can render in the right language.
  const iframeSrc = resolvedSrcPath
    ? `${resolvedSrcPath}?lang=${encodeURIComponent(lang)}`
    : "";

  return (
    <main
      className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]"
      data-testid={`${key}-page`}
    >
      <Navbar />

      <section className="px-5 pt-24 pb-6 md:px-8 md:pt-28 md:pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-[32px] border border-black/6 bg-white px-6 py-7 shadow-[0_22px_60px_rgba(16,24,40,0.07)] md:px-8 md:py-8 dark:border-white/10 dark:bg-[#121212]">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#F37021]/20 bg-[#FFF3EA] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#C95D13] dark:bg-[#1A1A1A] dark:text-[#F8A056]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F37021]" />
                  {label}
                </span>
                <h1
                  className="mt-4 font-display text-3xl font-bold leading-[1.05] text-[#111] sm:text-4xl md:text-5xl dark:text-white"
                  data-testid={`${key}-title`}
                >
                  {label}
                </h1>
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                <Link
                  to={resolvedBackHref}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-4 text-sm font-medium text-[#111] transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:text-[#F37021] shadow-[0_10px_24px_rgba(16,24,40,0.04)] sm:w-auto dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                  data-testid={`${key}-back`}
                >
                  <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                  {common.back || "Back"}
                </Link>

                <button
                  onClick={handleCopy}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-4 text-sm font-medium text-[#111] transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:text-[#F37021] shadow-[0_10px_24px_rgba(16,24,40,0.04)] sm:w-auto dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                  data-testid={`${key}-copy-link`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? (common.copied || "Copied!") : (common.copyLink || "Copy link")}</span>
                </button>
                <button
                  onClick={handleShare}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#F37021] px-4 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-[#E06015] shadow-[0_12px_26px_rgba(243,112,33,0.24)] sm:w-auto"
                  data-testid={`${key}-share`}
                >
                  <Share2 className="w-4 h-4" />
                  <span>{common.share || "Share"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded HTML — auto-resized to its content height so the BROWSER
          scrolls instead of an internal iframe scrollbar. The wrapper has
          no visible border / shadow / rounded corners so the embedded
          content reads as a natural part of the page flow. */}
      <section className="px-5 pb-12 md:px-8 md:pb-16">
        <div className="max-w-7xl mx-auto">
          {isInlineCalculator ? (
            <ShippingCalculator country={EMBED_CALCULATOR_COUNTRY} />
          ) : resolvedSrcPath ? (
            <div
              className="w-full overflow-hidden rounded-[32px] border border-black/6 bg-white shadow-[0_22px_60px_rgba(16,24,40,0.06)] dark:border-white/10 dark:bg-[#121212]"
              data-testid={`${key}-frame-wrapper`}
            >
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                title={label}
                loading="eager"
                scrolling="no"
                className="w-full block"
                style={{ height: `${iframeHeight}px`, border: 0, background: "transparent" }}
                data-testid={`${key}-iframe`}
              />
            </div>
          ) : (
            <div className="rounded-[32px] border border-black/6 bg-white px-6 py-10 text-center shadow-[0_22px_60px_rgba(16,24,40,0.06)] dark:border-white/10 dark:bg-[#121212]">
              <h2 className="font-display text-2xl font-semibold text-[#111] dark:text-white">
                {label}
              </h2>
              <p className="mt-3 text-sm text-[#666] dark:text-[#BAC4D4]">
                The requested embed is unavailable.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
